import {
    Activity,
    type ComponentPropsWithRef,
    createContext,
    type MouseEvent,
    type PropsWithChildren,
    useEffect,
    useState,
} from "react";
import useInitializedContext from "@/hooks/use-initialized-context.ts";
import type { Nullable } from "@/types";
import { cn } from "@/utils/style.ts";
import { isDefined } from "@/utils/types.ts";
import Button from "./button.tsx";
import Icon from "./icon.tsx";

type CollapseContextType = {
    open: boolean;
    toggle: () => void;
};

const CollapseContext = createContext<Nullable<CollapseContextType>>(null);

const useCollapse = () => useInitializedContext(CollapseContext, "Collapse");

type CollapseProps = PropsWithChildren<{
    open?: boolean;
    initialOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    className?: string;
}>;
function Collapse({ children, open: controlledOpen, initialOpen, onOpenChange, className }: CollapseProps) {
    const [open, setOpen] = useState<boolean>(controlledOpen ?? initialOpen ?? false);

    const toggle = () => {
        if (typeof controlledOpen !== "undefined") return;

        const newOpen = !open;

        setOpen(newOpen);
        onOpenChange?.(newOpen);
    };

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        if (isDefined(controlledOpen)) setOpen(controlledOpen);
    }, [controlledOpen]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        if (isDefined(initialOpen) && !isDefined(controlledOpen)) setOpen(initialOpen);
    }, [controlledOpen, initialOpen]);

    return (
        <CollapseContext value={{ open, toggle }}>
            <div className={cn("collapse", className)}>{children}</div>
        </CollapseContext>
    );
}

type CollapseTriggerProps = ComponentPropsWithRef<"button">;
function CollapseTrigger({ children, className, onClick, ...props }: CollapseTriggerProps) {
    const { toggle, open } = useCollapse();

    const handleClick = (evt: MouseEvent<HTMLButtonElement>) => {
        toggle();
        onClick?.(evt);
    };

    return (
        <Button {...props} onClick={handleClick} className={cn("collapse__trigger", className)}>
            <Icon name="triangle-down" className={cn("collapse__trigger__icon", open && "open")} />
            {children}
        </Button>
    );
}

function CollapseDetails({ children, className, ...props }: ComponentPropsWithRef<"span">) {
    return (
        <span {...props} className={cn("collapse__details", className)}>
            {children}
        </span>
    );
}

function CollapseContentInner({ children, className, ...props }: ComponentPropsWithRef<"div">) {
    return (
        <div {...props} className={cn("collapse__content", className)}>
            {children}
        </div>
    );
}

function CollapseContent({ children, ...props }: ComponentPropsWithRef<"div">) {
    const { open } = useCollapse();

    return (
        <Activity mode={open ? "visible" : "hidden"}>
            <CollapseContentInner {...props}>{children}</CollapseContentInner>
        </Activity>
    );
}

export { Collapse, CollapseTrigger, CollapseDetails, CollapseContent };
